import { pontosDeAncoragem } from './ancoragem'
import { contornosDaCorda } from './contornoCorda'
import { percursoNoMundo } from './cordas'
import { calcularAncoragem } from './ancoragens'
import { ancoragensDeFitas, type AncoragemDeFitas } from './ancoragensDeFitas'
import { CONFIGURACAO_AVISOS_PADRAO, type ConfiguracaoAvisos } from './configuracaoAvisos'
import { analisarEnvolvimento } from './envolvimento'
import { ANGULO_CARGA_LATERAL_GRAUS, orientacaoDoMosquetao } from './mosquetao'
import { definicaoDaFuncao } from './funcoesCorda'
import { simularQuedaAnimada } from './quedaAnimada'
import { analisarTalabartes } from './quedaEmAltura'
import type { AncoragemTabletop, CenaTabletop, CordaTabletop, ObjetoTabletop, ReferenciaPonto } from './tipos'

/**
 * Avisos didáticos da cena. O app não bloqueia nada: só aponta o que um
 * instrutor olharia numa montagem de salvamento.
 *
 * - ponta solta: um ponto da corda ou da ancoragem deixou de existir;
 * - corda sem função: foi presa nos pontos e ainda não se escolheu o que ela é;
 * - corda que não termina num ponto de ancoragem;
 * - corda que dobra numa quina de um objeto (ponto de pressão) sem protetor de corda;
 * - abertura da ancoragem em V, W ou M acima do limite configurado;
 * - içamento sem corda de segurança;
 * - escada sem nenhuma estabilização;
 * - talabarte do trabalhador: queda que bate no chão, talabarte curto demais para a
 *   distância, fator de queda alto, queda livre sem absorvedor de energia e balanço
 *   (pêndulo) que bate na estrutura.
 *
 * TypeScript puro: sem React e sem Three.js.
 */

export type TipoAviso =
  | 'ponta-solta'
  | 'corda-sem-funcao'
  | 'corda-em-aresta'
  | 'corda-sem-ancoragem'
  | 'angulo-ancoragem'
  | 'angulo-envolvimento'
  | 'mosquetao-carga-lateral'
  | 'icamento-sem-seguranca'
  | 'escada-sem-estabilizacao'
  | 'queda-bate-no-chao'
  | 'talabarte-curto'
  | 'fator-de-queda-alto'
  | 'talabarte-sem-absorvedor'
  | 'balanco-bate-na-estrutura'
  | 'ruptura-de-talabarte'
  | 'estaca-puxada-no-topo'

export interface AvisoTabletop {
  /** Estável entre cálculos: a mesma situação gera o mesmo id. */
  readonly id: string
  readonly tipo: TipoAviso
  readonly mensagem: string
  readonly idCorda?: string
  readonly idAncoragem?: string
  readonly idObjeto?: string
}

const nomeDaCorda = (corda: CordaTabletop) => corda.rotulo ?? definicaoDaFuncao(corda.funcao).rotulo
const nomeDaAncoragem = (a: AncoragemTabletop) => a.rotulo ?? ('idsFitas' in a ? `O ${a.tipo} de fitas` : `Ancoragem em ${a.tipo}`)
const formatarGraus = (valor: number) => `${Math.round(valor)}°`
const formatarPorcento = (fracao: number) => `${Math.round(fracao * 100)}%`

function descreverPonto(cena: CenaTabletop, referencia: ReferenciaPonto): string {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  const ponto = objeto && pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  return `${objeto?.nome ?? referencia.idObjeto} · ${ponto?.nome ?? referencia.idPonto}`
}

const existe = (cena: CenaTabletop, referencia: ReferenciaPonto) => {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  return Boolean(objeto && pontosDeAncoragem(objeto).some((p) => p.id === referencia.idPonto))
}

/** Os objetos em que uma corda ou ancoragem toca, para saber se algo continua por ali. */
function objetosTocadosPorOutros(cena: CenaTabletop, exceto: { corda?: string; ancoragem?: string }): Map<string, number> {
  const contagem = new Map<string, number>()
  const marcar = (idObjeto: string) => contagem.set(idObjeto, (contagem.get(idObjeto) ?? 0) + 1)
  for (const corda of cena.cordas ?? []) {
    if (corda.id === exceto.corda) continue
    for (const id of new Set(corda.percurso.map((p) => p.idObjeto))) marcar(id)
  }
  for (const a of cena.ancoragens ?? []) {
    if (a.id === exceto.ancoragem) continue
    for (const id of new Set([a.centro.idObjeto, ...a.fixacoes.map((p) => p.idObjeto)])) marcar(id)
  }
  return contagem
}

/** A corda foi presa nos pontos, mas ainda não se escolheu o que ela é: só um lembrete, sem bloquear. */
function avisosDeCordasSemFuncao(cena: CenaTabletop): AvisoTabletop[] {
  return (cena.cordas ?? [])
    .filter((c) => c.funcao === 'livre')
    .map((corda) => ({
      id: `corda-sem-funcao:${corda.id}`,
      tipo: 'corda-sem-funcao' as const,
      idCorda: corda.id,
      mensagem: `${corda.rotulo ?? 'Uma corda'} ainda não tem função: toque nela e escolha o que ela é (tração, estabilização, tirolesa, segurança…).`,
    }))
}

/** A corda estica por cima de uma quina viva: ali a fibra se desgasta, e um protetor de corda resolve. */
function avisosDeCordasEmAresta(cena: CenaTabletop): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  for (const corda of cena.cordas ?? []) {
    if (corda.funcao === 'fita' || corda.funcao === 'talabarte') continue
    const desprotegidos = new Set<string>()
    for (const trecho of contornosDaCorda(cena, corda, percursoNoMundo(cena, corda))) {
      for (const p of trecho?.pressoes ?? []) if (!p.protegido) desprotegidos.add(p.idObjeto)
    }
    for (const idObjeto of desprotegidos) {
      const objeto = cena.objetos.find((o) => o.id === idObjeto)
      avisos.push({
        id: `corda-em-aresta:${corda.id}:${idObjeto}`,
        tipo: 'corda-em-aresta',
        idCorda: corda.id,
        idObjeto,
        mensagem: `${nomeDaCorda(corda)} dobra numa quina de ${objeto?.nome ?? 'um objeto'}: ponto de pressão, a fibra se desgasta. Ponha um protetor de corda na quina ou desvie a corda.`,
      })
    }
  }
  return avisos
}

function avisosDePontasSoltas(cena: CenaTabletop): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  for (const corda of cena.cordas ?? []) {
    const soltos = corda.percurso.filter((p) => !existe(cena, p))
    if (soltos.length === 0) continue
    avisos.push({
      id: `ponta-solta:corda:${corda.id}`,
      tipo: 'ponta-solta',
      idCorda: corda.id,
      mensagem: `${nomeDaCorda(corda)} tem ${soltos.length === 1 ? 'uma ponta solta' : `${soltos.length} pontas soltas`}: ${soltos.map((p) => p.idPonto).join(', ')} não existe mais (o objeto mudou de forma).`,
    })
  }
  for (const a of cena.ancoragens ?? []) {
    const soltos = [...a.fixacoes, a.centro].filter((p) => !existe(cena, p))
    if (soltos.length === 0) continue
    avisos.push({
      id: `ponta-solta:ancoragem:${a.id}`,
      tipo: 'ponta-solta',
      idAncoragem: a.id,
      mensagem: `${nomeDaAncoragem(a)} perdeu ${soltos.length === 1 ? 'um ponto' : `${soltos.length} pontos`}: ${soltos.map((p) => p.idPonto).join(', ')} não existe mais (o objeto mudou de forma).`,
    })
  }
  return avisos
}

/**
 * Uma ponta de corda termina bem quando (a) é um ponto de ancoragem ou de carga,
 * (b) outra corda ou ancoragem passa pelo mesmo objeto — a corda segue por
 * dentro do equipamento, como numa polia ou num bloqueador — ou (c) é a ponta
 * por onde se puxa, marcada pela seta de sentido.
 */
function avisosDeCordasSemAncoragem(cena: CenaTabletop): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  for (const corda of cena.cordas ?? []) {
    const tocados = objetosTocadosPorOutros(cena, { corda: corda.id })
    const ultimo = corda.percurso.length - 1
    for (const indice of [0, ultimo]) {
      const referencia = corda.percurso[indice]!
      if (!existe(cena, referencia)) continue // já é uma ponta solta
      const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)!
      const ponto = pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)!
      const ehPonta = (corda.sentido === 'paraFim' && indice === ultimo) || (corda.sentido === 'paraInicio' && indice === 0)
      if (ponto.uso !== 'passagem' || tocados.has(objeto.id) || ehPonta) continue
      avisos.push({
        id: `corda-sem-ancoragem:${corda.id}:${indice === 0 ? 'inicio' : 'fim'}`,
        tipo: 'corda-sem-ancoragem',
        idCorda: corda.id,
        idObjeto: objeto.id,
        mensagem: `${nomeDaCorda(corda)} termina em ${descreverPonto(cena, referencia)}, que não é um ponto de ancoragem, e nada continua por ali.`,
      })
    }
  }
  return avisos
}

function avisosDeAnguloDaAncoragem(cena: CenaTabletop, config: ConfiguracaoAvisos): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  // as desenhadas e as reconhecidas das fitas (duas fitas no mesmo mosquetão são um V)
  for (const a of [...(cena.ancoragens ?? []), ...ancoragensDeFitas(cena)]) {
    const r = calcularAncoragem(cena, a)
    if (r.incompleta) continue
    // a reconhecida não existe na lista de ancoragens: o aviso leva para a primeira fita dela
    const alvo = 'idsFitas' in a ? { idCorda: (a as AncoragemDeFitas).idsFitas[0]! } : { idAncoragem: a.id }
    if (r.degenerada) {
      avisos.push({
        id: `angulo-ancoragem:${a.id}`,
        tipo: 'angulo-ancoragem',
        ...alvo,
        mensagem: `${nomeDaAncoragem(a)}: as pernas estão quase opostas (${formatarGraus(r.anguloMaximoGraus!)}) e a carga por perna dispara. Aproxime os pontos de fixação.`,
      })
    } else if (r.anguloMaximoGraus! > config.anguloMaximoAncoragemGraus) {
      avisos.push({
        id: `angulo-ancoragem:${a.id}`,
        tipo: 'angulo-ancoragem',
        ...alvo,
        mensagem: `${nomeDaAncoragem(a)} abre ${formatarGraus(r.anguloMaximoGraus!)} entre as pernas (limite ${formatarGraus(config.anguloMaximoAncoragemGraus)}): ${r.equalizacao === 'pre-equalizada' ? 'a perna mais carregada recebe' : 'cada perna recebe'} cerca de ${formatarPorcento(r.fracaoPorPerna!)} da carga.`,
      })
    }
  }
  return avisos
}

function avisosDeIcamentoSemSeguranca(cena: CenaTabletop): AvisoTabletop[] {
  const cordas = cena.cordas ?? []
  const icamentos = cordas.filter((c) => c.funcao === 'tracao')
  if (icamentos.length === 0 || cordas.some((c) => c.funcao === 'seguranca')) return []
  return [
    {
      id: 'icamento-sem-seguranca',
      tipo: 'icamento-sem-seguranca',
      idCorda: icamentos[0]!.id,
      mensagem: 'Há corda de tração, mas nenhuma corda de segurança: se o sistema principal falhar, nada segura a carga.',
    },
  ]
}

/** Junta os objetos que uma corda de fita ou de estabilização liga, para saber o que está preso a quê. */
function avisosDeEscadaSemEstabilizacao(cena: CenaTabletop): AvisoTabletop[] {
  const escadas = cena.objetos.filter((o) => o.tipo === 'escada' && !o.oculto)
  if (escadas.length === 0) return []

  const pai = new Map<string, string>()
  const raiz = (id: string): string => {
    const p = pai.get(id) ?? id
    if (p === id) return id
    const r = raiz(p)
    pai.set(id, r)
    return r
  }
  const unir = (a: string, b: string) => pai.set(raiz(a), raiz(b))

  const estabilizacoes = (cena.cordas ?? []).filter((c) => c.funcao === 'estabilizacao')
  for (const corda of (cena.cordas ?? []).filter((c) => c.funcao === 'fita' || c.funcao === 'estabilizacao')) {
    const [primeiro, ...resto] = corda.percurso.map((p) => p.idObjeto)
    for (const id of resto) unir(primeiro!, id)
  }
  const raizesComEstabilizacao = new Set(estabilizacoes.map((c) => raiz(c.percurso[0]!.idObjeto)))

  return escadas
    .filter((e: ObjetoTabletop) => !raizesComEstabilizacao.has(raiz(e.id)))
    .map((e) => ({
      id: `escada-sem-estabilizacao:${e.id}`,
      tipo: 'escada-sem-estabilizacao' as const,
      idObjeto: e.id,
      mensagem: `${e.nome} não tem nenhuma corda de estabilização: nada a segura se a carga puxar de lado.`,
    }))
}

const metros = (valor: number) => `${valor.toFixed(2).replace('.', ',')} m`

/** Os avisos do trabalho em altura: um por problema de cada talabarte. Os números vêm de `analisarQueda`. */
function avisosDeTalabarte(cena: CenaTabletop): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  for (const t of analisarTalabartes(cena)) {
    const trabalhador = cena.objetos.find((o) => o.id === t.idTrabalhador)!
    const r = t.resultado
    const base = { idCorda: t.idCorda, idObjeto: t.idTrabalhador }
    if (!r.alcanca) {
      avisos.push({
        ...base,
        id: `talabarte-curto:${t.idCorda}`,
        tipo: 'talabarte-curto',
        mensagem: `O talabarte de ${trabalhador.nome} tem ${metros(r.parcelasZlq.talabarteM)}, mas a ancoragem fica a ${metros(r.distanciaM)} do engate: ele não alcança. Aproxime o trabalhador ou use um talabarte maior.`,
      })
    }
    if (r.baterNoChao) {
      avisos.push({
        ...base,
        id: `queda-bate-no-chao:${t.idCorda}`,
        tipo: 'queda-bate-no-chao',
        mensagem: `A zona livre de queda de ${trabalhador.nome} é ${metros(r.zonaLivreM)}, mas só há ${metros(r.alturaLivreM)} da ancoragem até a superfície abaixo: faltam ${metros(-r.folgaM)}. Ele bateria antes de o sistema parar a queda.`,
      })
    }
    if (r.fatorDesfavoravel) {
      avisos.push({
        ...base,
        id: `fator-de-queda-alto:${t.idCorda}`,
        tipo: 'fator-de-queda-alto',
        mensagem: `Fator de queda ${r.fatorDeQueda.toFixed(2).replace('.', ',')} em ${trabalhador.nome}: a ancoragem está abaixo do engate e a queda livre é de ${metros(r.quedaLivreM)}. Prenda numa ancoragem mais alta, acima do engate.`,
      })
    }
    if (r.semAbsorvedorComQuedaLivreAlta) {
      avisos.push({
        ...base,
        id: `talabarte-sem-absorvedor:${t.idCorda}`,
        tipo: 'talabarte-sem-absorvedor',
        mensagem: `${trabalhador.nome} está sem absorvedor de energia e teria queda livre de ${metros(r.quedaLivreM)}: o talabarte sem absorvedor só serve onde não há queda livre relevante.`,
      })
    }
    const fitaDanificada = trabalhador.parametros?.fitaDanificada === 1
    const limiteRupturaKN = fitaDanificada ? 4 : (trabalhador.parametros?.cargaRupturaKN ?? 22)
    const picoPrevistoKN = t.choque?.resultado?.forcaPicoKN ?? 0
    if (fitaDanificada || (picoPrevistoKN > 0 && picoPrevistoKN >= limiteRupturaKN)) {
      avisos.push({
        ...base,
        id: `ruptura-de-talabarte:${t.idCorda}`,
        tipo: 'ruptura-de-talabarte',
        mensagem: fitaDanificada
          ? `Alerta crítico em ${trabalhador.nome}: fita marcada como danificada (aresta viva/desgaste). O talabarte romperá a cerca de 4 kN em caso de queda!`
          : `Risco de ruptura em ${trabalhador.nome}: a força de pico calculada (${picoPrevistoKN.toFixed(1).replace('.', ',')} kN) atinge ou supera a carga de ruptura (${limiteRupturaKN} kN). O talabarte romperá!`,
      })
    }
    const animada = simularQuedaAnimada(cena, t.idCorda)
    if (animada?.impacto) {
      const i = animada.impacto
      avisos.push({
        ...base,
        id: `balanco-bate-na-estrutura:${t.idCorda}`,
        tipo: 'balanco-bate-na-estrutura',
        mensagem:
          i.fase === 'pendulo'
            ? `Se ${trabalhador.nome} cair, o balanço bate em ${i.alvo} a ${i.velocidadeMs.toFixed(1).replace('.', ',')} m/s: a ancoragem está deslocada da borda e a queda vira pêndulo. Prenda mais perto da vertical do ponto de queda.`
            : `Se ${trabalhador.nome} cair, bate em ${i.alvo} a ${i.velocidadeMs.toFixed(1).replace('.', ',')} m/s antes de o talabarte parar a queda.`,
      })
    }
  }
  return avisos
}

/** A fita em cesta (ou três voltas) com o mosquetão perto demais do tronco: o ângulo entre as pernas passa do limite. */
function avisosDeEnvolvimento(cena: CenaTabletop, config: ConfiguracaoAvisos): AvisoTabletop[] {
  return cena.objetos
    .filter((o) => o.tipo === 'envolvimento' && !o.oculto)
    .flatMap((o) => {
      const a = analisarEnvolvimento(o)
      if (a.anguloEntrePernasGraus === null || a.anguloEntrePernasGraus <= config.anguloMaximoAncoragemGraus) return []
      return [
        {
          id: `angulo-envolvimento:${o.id}`,
          tipo: 'angulo-envolvimento' as const,
          idObjeto: o.id,
          mensagem: `${o.nome} abre ${formatarGraus(a.anguloEntrePernasGraus)} entre as pernas (limite ${formatarGraus(config.anguloMaximoAncoragemGraus)}): cada perna recebe cerca de ${formatarPorcento(a.fracaoPorPerna)} da carga. Afaste o mosquetão do tronco com uma fita mais longa.`,
        },
      ]
    })
}

/** O mosquetão puxado de lado: alguma corda ou perna fica longe do eixo maior (carga triaxial). */
function avisosDeMosquetao(cena: CenaTabletop): AvisoTabletop[] {
  return cena.objetos
    .filter((o) => o.tipo === 'mosquetao' && !o.oculto)
    .flatMap((o) => {
      const r = orientacaoDoMosquetao(cena, o)
      if (!r.cargaLateral) return []
      return [
        {
          id: `mosquetao-carga-lateral:${o.id}`,
          tipo: 'mosquetao-carga-lateral' as const,
          idObjeto: o.id,
          mensagem: `${o.nome} é puxado de lado: uma corda ou perna fica a ${formatarGraus(r.maiorDesvioGraus)} do eixo maior (mais de ${formatarGraus(ANGULO_CARGA_LATERAL_GRAUS)}). Fora do eixo maior o mosquetão resiste bem menos; alinhe as puxadas ou use uma placa de ancoragem.`,
        },
      ]
    })
}

/** A estaca recebe a carga no topo em vez da base rente ao solo: braço de alavanca excessivo. */
function avisosDeEstaca(cena: CenaTabletop): AvisoTabletop[] {
  const avisos: AvisoTabletop[] = []
  const estacas = cena.objetos.filter((o) => o.tipo === 'estaca' && !o.oculto)
  if (estacas.length === 0) return avisos

  for (const estaca of estacas) {
    for (const corda of cena.cordas ?? []) {
      const tocaNoTopo = corda.percurso.some(
        (p) => p.idObjeto === estaca.id && (p.idPonto === 'topo' || p.idPonto === 'topo-2' || p.idPonto === 'topo-3'),
      )
      if (!tocaNoTopo) continue

      const ligaOutraEstaca = corda.percurso.some(
        (p) => p.idObjeto !== estaca.id && cena.objetos.find((o) => o.id === p.idObjeto)?.tipo === 'estaca',
      )
      if (ligaOutraEstaca && corda.funcao === 'fita') continue

      if (corda.funcao === 'tracao' || corda.funcao === 'tirolesa' || corda.funcao === 'seguranca' || corda.sentido) {
        avisos.push({
          id: `estaca-puxada-no-topo:${estaca.id}:${corda.id}`,
          tipo: 'estaca-puxada-no-topo',
          idObjeto: estaca.id,
          idCorda: corda.id,
          mensagem: `${estaca.nome}: a corda de tração está presa no topo da estaca. O esforço deve ser aplicado rente ao solo (no ponto 'base') para evitar o braço de alavanca que pode arrancar a estaca do chão. O topo deve ser usado apenas para amarração e travamento tandem.`,
        })
      }
    }
  }
  return avisos
}

export function avisosDaCena(cena: CenaTabletop, config: ConfiguracaoAvisos = CONFIGURACAO_AVISOS_PADRAO): readonly AvisoTabletop[] {
  return [
    ...avisosDePontasSoltas(cena),
    ...avisosDeCordasSemFuncao(cena),
    ...avisosDeCordasEmAresta(cena),
    ...avisosDeCordasSemAncoragem(cena),
    ...avisosDeAnguloDaAncoragem(cena, config),
    ...avisosDeEnvolvimento(cena, config),
    ...avisosDeMosquetao(cena),
    ...avisosDeIcamentoSemSeguranca(cena),
    ...avisosDeEscadaSemEstabilizacao(cena),
    ...avisosDeTalabarte(cena),
    ...avisosDeEstaca(cena),
  ]
}
